import type { ComparisonRun } from 'fuel-benchmark-contracts';
import { Checkbox } from '../../components/ui/Checkbox';
import { StatusText } from '../../components/ui/StatusText';
import { formatDay, formatDecimal } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { runStatusTone } from '../runs/run-display';

export interface RunPickerProps {
  vehicleId: string;
  vehicleLabel: string;
  vehicleCode: string;
  runs: readonly ComparisonRun[];
  selectedRunIds: readonly string[];
  unit: string;
  onToggle: (vehicleId: string, runId: string) => void;
}

/**
 * Step C (plan §7): every valid run of the vehicle under the chosen scenario, all checked to begin with.
 * Deselecting one narrows the aggregation, which is the whole point of multi-run comparison — the last
 * remaining run cannot be unchecked, because a vehicle with no runs has nothing to compare.
 */
export const RunPicker = ({ vehicleId, vehicleLabel, vehicleCode, runs, selectedRunIds, unit, onToggle }: RunPickerProps) => {
  const { t } = useI18nTranslation();

  const selected = new Set(selectedRunIds);
  const isLastSelected = selected.size <= 1;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="truncate text-sm font-medium text-card-foreground">{vehicleLabel}</h3>
        <p className="shrink-0 font-mono text-xs text-muted-foreground">{vehicleCode}</p>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        {selected.size === 1 ? t('compare.result.runsOne') : t('compare.result.runsMany', { count: selected.size })}
        {' / '}
        {runs.length}
      </p>

      <ul className="mt-3 space-y-2">
        {runs.length === 0 ? <li className="text-sm text-muted-foreground">{t('compare.runs.empty')}</li> : null}

        {runs.map((run) => {
          const isSelected = selected.has(run.id);

          return (
            <li key={run.id}>
              <label
                htmlFor={`compare-run-${run.id}`}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-background px-3 py-2 transition-colors hover:bg-muted"
              >
                <Checkbox
                  id={`compare-run-${run.id}`}
                  className="mt-0.5"
                  checked={isSelected}
                  disabled={isSelected && isLastSelected}
                  onChange={() => onToggle(vehicleId, run.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm text-foreground">{formatDay(run.testDate)}</span>
                    <span className="whitespace-nowrap text-sm font-medium text-foreground">
                      {run.result === null ? t('common.notSet') : `${formatDecimal(run.result, 1)} ${unit}`}
                    </span>
                  </span>
                  <span className="mt-0.5 flex flex-wrap items-baseline justify-between gap-2">
                    <span className="truncate font-mono text-xs text-muted-foreground">{run.routeCode ?? t('common.notSet')}</span>
                    <StatusText
                      className="text-xs"
                      label={t(`enums.runStatus.${run.status}`)}
                      tone={runStatusTone(run.status)}
                    />
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
